import { byId, clear, el, icon, setHidden } from "../dom";
import { formatCount, formatDayTime, formatRelative } from "../format";
import type { OperatorStats } from "../types";
import type { LoadStatus } from "./operation";

export interface StatsState {
  status: LoadStatus;
  stats: OperatorStats | null;
  error: string | null;
}

export interface StatsHandlers {
  onRetry(): void;
}

export interface StatsController {
  setState(state: StatsState): void;
}

interface StatSlot {
  card: HTMLElement;
  value: HTMLElement;
  meta: HTMLElement;
}

const BUMP_MS = 900;

function slot(cardId: string): StatSlot {
  const card = byId<HTMLElement>(cardId);
  return {
    card,
    value: byId<HTMLElement>(`${cardId}-value`),
    meta: byId<HTMLElement>(`${cardId}-meta`),
  };
}

export function initStats(handlers: StatsHandlers): StatsController {
  const households = slot("stat-households");
  const alertsToday = slot("stat-alerts");
  const trucks = slot("stat-trucks");
  const lastAlert = slot("stat-last-alert");
  const errorRow = byId<HTMLParagraphElement>("stats-error");

  const errorText = el("span");
  const retry = el("button", { className: "btn btn--ghost btn--sm" });
  retry.type = "button";
  retry.append(icon("i-refresh"), el("span", { text: "Reintentar" }));
  retry.addEventListener("click", handlers.onRetry);
  errorRow.append(icon("i-alert"), errorText, retry);

  const previous = new Map<StatSlot, string>();
  const bumpTimers = new Map<StatSlot, number>();

  function setSlot(current: StatSlot, value: string, meta: string, asText = false): void {
    const changed = previous.has(current) && previous.get(current) !== value;
    previous.set(current, value);
    current.value.textContent = value;
    current.value.classList.toggle("stat__value--text", asText);
    current.meta.textContent = meta;
    if (!changed) return;
    const timer = bumpTimers.get(current);
    if (timer !== undefined) window.clearTimeout(timer);
    current.card.classList.add("stat--bump");
    bumpTimers.set(
      current,
      window.setTimeout(() => current.card.classList.remove("stat--bump"), BUMP_MS),
    );
  }

  function setLoading(current: StatSlot): void {
    previous.delete(current);
    current.value.classList.remove("stat__value--text");
    current.value.textContent = "";
    current.meta.textContent = "";
    current.card.classList.add("stat--loading");
  }

  function setError(current: StatSlot): void {
    current.card.classList.remove("stat--loading");
    if (!previous.has(current)) {
      current.value.classList.remove("stat__value--text");
      current.value.textContent = "—";
      current.meta.textContent = "Sin datos";
    }
  }

  return {
    setState(state) {
      setHidden(errorRow, state.error === null);
      if (state.error !== null) {
        clear(errorText);
        errorText.textContent = state.error;
      }

      if (state.status === "idle" || (state.status === "loading" && state.stats === null)) {
        for (const current of [households, alertsToday, trucks, lastAlert]) setLoading(current);
        return;
      }

      const stats = state.stats;
      if (stats === null) {
        for (const current of [households, alertsToday, trucks, lastAlert]) setError(current);
        return;
      }

      for (const current of [households, alertsToday, trucks, lastAlert]) {
        current.card.classList.remove("stat--loading");
      }

      setSlot(households, formatCount(stats.households_total), "Hogares con avisos configurados");
      setSlot(
        alertsToday,
        formatCount(stats.alerts_today),
        `${formatCount(stats.alerts_total)} en total`,
      );
      setSlot(trucks, formatCount(stats.sessions_active), "Con señal en los últimos 5 min");
      if (stats.last_alert_at === null) {
        setSlot(lastAlert, "Sin alertas", "Aún no se ha enviado ninguna", true);
      } else {
        setSlot(lastAlert, formatRelative(stats.last_alert_at), formatDayTime(stats.last_alert_at), true);
      }
    },
  };
}
